/**
 * Cart drawer
 *
 * - Opens on click of any [data-cart-toggle] element (e.g. the header cart icon)
 * - Closes on backdrop click, [data-cart-close], or Escape
 * - Quantity +/- and Remove update via /cart/change.js
 * - Add/change use the Section Rendering API (sections param) so the drawer
 *   markup comes back in the same request — no extra /?sections or /cart.js calls.
 * - window.cartDrawer.addItem() opens the drawer at once in an "Adding…" state, adds
 *   to cart, then shows the new item, or the Shopify Ajax API error on failure.
 */
(() => {
  const SECTION_ID = 'cart-drawer';
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  let lastFocused = null;
  // Last known cart size — seeded by Liquid, updated on every drawer render.
  let itemCount = parseInt(drawer.dataset.itemCount, 10);

  const isOpen = () => drawer.dataset.open === 'true';

  const clearError = () => {
    const el = drawer.querySelector('[data-cart-error]');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
  };

  function open(keepError) {
    if (!keepError) clearError();
    // Commit the current (closed) styles so the slide-in always animates,
    // even when open() runs in the same task as an innerHTML swap.
    void drawer.offsetWidth;
    drawer.dataset.open = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    const closeBtn = drawer.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  const close = () => {
    drawer.dataset.open = 'false';
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocused) lastFocused.focus();
  };

  const showError = (message) => {
    const el = drawer.querySelector('[data-cart-error]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    open(true);
  };

  const setLoading = (state) => {
    drawer.dataset.loading = state ? 'true' : 'false';
  };

  const setAdding = (state) => {
    drawer.dataset.adding = state ? 'true' : 'false';
  };

  // True when the cart already holds exactly this single selection
  // (same variant, selling plan, quantity and VSL _vsl_order note, nothing else).
  // The note counts too, so a stale value is replaced (2026-10-16: added through
  // the video page's button, back later through an ad, same plan: "true" stays).
  const cartHolds = (items) => {
    if (items.length !== 1 || itemCount !== Number(items[0].quantity)) return false;
    const lines = drawer.querySelectorAll('[data-cart-item]');
    if (lines.length !== 1) return false;
    const line = lines[0].dataset;
    const props = items[0].properties || {};
    return (
      line.variantId === String(items[0].id) &&
      line.quantity === String(items[0].quantity) &&
      (line.sellingPlanId || '') === String(items[0].selling_plan || '') &&
      (line.vslOrder || '') === String(props._vsl_order || '')
    );
  };

  // Todd's ad attribution (ws-attribution-capture.js) rides on the add call:
  // that script's own write on the Checkout click is canceled by the checkout
  // navigation (measured 2026-09-22). Same keys and same wai-only gate as its
  // writeNoteAttributes().
  const wsAttributes = () => {
    try {
      const attr = window._wsGetAttribution && window._wsGetAttribution();
      if (!attr || !attr.wai) return null;
      return {
        ws_source: attr.ws || '',
        ws_funnel: attr.wf || '',
        ws_ad_id: attr.wai || '',
        ws_campaign_id: attr.wci || '',
        ws_adset_id: attr.wsi || '',
        ws_campaign: attr.wc || '',
        ws_adset: attr.wst || '',
        ws_ad: attr.wa || '',
        ws_captured_at: new Date(attr.ts).toISOString(),
        ws_capture_url: attr.url || '',
      };
    } catch (err) {
      return null; // attribution only — never block the add
    }
  };

  const syncCount = (count) => {
    if (count == null || Number.isNaN(count)) return;
    itemCount = count;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  const render = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-cart-drawer]');
    if (!next) return;
    const wasOpen = isOpen();
    drawer.innerHTML = next.innerHTML;
    drawer.dataset.open = wasOpen ? 'true' : 'false';
    drawer.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');
    syncCount(parseInt(next.dataset.itemCount, 10));
  };

  // ATC telemetry → Todd's collector. Payload shape mirrors scroll-pixel-snippet.js
  // (same client_id key so events join up). Must never break add-to-cart.
  const COLLECTOR_URL = 'https://gomd-collector-production.up.railway.app/api/events/collect';
  const track = (eventType, extra) => {
    try {
      const uuid = () =>
        crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
      let cid = localStorage.getItem('_gomd_cid');
      if (!cid) {
        cid = uuid();
        localStorage.setItem('_gomd_cid', cid);
      }
      const payload = JSON.stringify({
        event_type: eventType,
        path: window.location.pathname,
        href: window.location.href,
        client_id: cid,
        event_id: uuid(),
        ts: Date.now(),
        device_class: /Mobile|Android|iP(hone|od|ad)/i.test(navigator.userAgent)
          ? 'mobile'
          : 'desktop',
        ...extra,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(COLLECTOR_URL, payload);
      } else {
        fetch(COLLECTOR_URL, { method: 'POST', body: payload, keepalive: true });
      }
    } catch (err) {
      /* telemetry only — swallow */
    }
  };

  const refreshDrawer = async () => {
    const res = await fetch(`/?sections=${SECTION_ID}`);
    if (!res.ok) return;
    const data = await res.json();
    if (data[SECTION_ID]) render(data[SECTION_ID]);
  };

  const addItem = async (item) => {
    clearError();
    const items = Array.isArray(item) ? item : [item];
    const variantId = items[0] && items[0].id;
    track('atc_attempt', { variant_id: variantId });
    // Already in the cart exactly as chosen (e.g. the drawer was closed and the
    // button clicked again): nothing to change, so skip both cart calls.
    if (cartHolds(items)) {
      open();
      return null;
    }
    // Open right away; the cart calls below take about a second.
    setAdding(true);
    open();
    try {
      let res;
      try {
        // Single-item cart: clear whatever is already there so only the new selection
        // remains. Skipped when the cart is known-empty to halve the cart mutations
        // per add (Shopify rate-limits these endpoints per IP); an unknown count
        // (NaN) still clears, to stay safe.
        if (itemCount !== 0) {
          const clearRes = await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          });
          if (!clearRes.ok) {
            track('atc_error', {
              status: clearRes.status,
              error_source: 'cart/clear.js',
              variant_id: variantId,
            });
          }
        }
        const body = { items, sections: SECTION_ID };
        const attributes = wsAttributes();
        if (attributes) body.attributes = attributes;
        res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (err) {
        track('atc_error', { status: 0, error_source: 'network', variant_id: variantId });
        showError('Could not add to cart.');
        throw err;
      }
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        track('atc_error', {
          status: res.status,
          error_source: 'cart/add.js',
          variant_id: variantId,
        });
        const message = (data && data.description) || 'Could not add to cart.';
        showError(message);
        throw new Error(message);
      }
      if (data && data.sections && data.sections[SECTION_ID]) {
        render(data.sections[SECTION_ID]);
      } else {
        await refreshDrawer();
      }
      open();
      return data;
    } finally {
      setAdding(false);
    }
  };

  const changeLine = async (id, quantity) => {
    setLoading(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity, sections: SECTION_ID }),
      });
      if (!res.ok) throw new Error(`cart/change.js ${res.status}`);
      const cart = await res.json();
      if (cart.sections && cart.sections[SECTION_ID]) {
        render(cart.sections[SECTION_ID]);
      } else {
        await refreshDrawer();
      }
      syncCount(cart.item_count);
    } catch (err) {
      console.error('[cart-drawer]', err);
    } finally {
      setLoading(false);
    }
  };

  // Single delegated click handler — survives innerHTML swaps without rebinding
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle) {
      e.preventDefault();
      lastFocused = toggle;
      open();
      return;
    }

    if (!drawer.contains(e.target)) return;

    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      close();
      return;
    }

    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const key = removeBtn.dataset.lineKey;
      if (!key) return; // sample line item
      changeLine(key, 0);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) close();
  });

  // Back/forward cache restores a snapshot of the drawer; re-read the real cart so
  // the item count and the already-in-cart check above stay true.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refreshDrawer().catch(() => {});
  });

  // Public API for custom add-to-cart buttons (e.g. the PDP buy box)
  window.cartDrawer = {
    open: () => open(),
    close,
    addItem,
    showError,
    refresh: () => refreshDrawer(),
  };
})();
